import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { array, number, object, string } from 'prop-types'
import styles from '../EventAgenda.module.scss'
import { TimelineBox, CurrentTimeIndicator } from '.'
import Type from '@/component/Primitive/Type'
import moment from 'moment'
import { DateTime } from 'luxon'

const gridSettingTypes = {
  gridColumns: number,
  gridRows: number,
  numTimeLabels: number,
  timelineWidth: number,
  timelineHeight: number,
  timelinePaddingTop: number,
  timelinePaddingBottom: number,
  timelineLabelsHeight: number,
  timelineLabelsWidth: number
}

export const Timeline = ({
  event,
  sortedAgenda,
  agendaItemDayMap,
  selectedDay
}) => {
  const { agenda, timezone, _registerButtonClassName, _registerAction } = event
  const timezoneAbbreviation = DateTime.fromISO(event.agenda[0].endDate, {
    zone: timezone
  }).toFormat('ZZZZ')

  // HELPERS & SETTINGS
  const getMinutesFromMidnight = (date) => {
    const d = new Date(date)
    return d.getUTCHours() * 60 + d.getUTCMinutes()
  }

  const formatDate = (dateString) => moment(dateString).format('D.M.YYYY')
  // --------------------------------

  // const sortedAgenda = useMemo(() => {
  //   return agenda.sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
  // }, [agenda])

  // const agendaItemDayMap = useMemo(() => {
  //   const formattedStartDates = Array.from(
  //     new Set(agenda.map((item) => formatDate(item.startDate)))
  //   )
  //   return agenda.reduce((acc, curr) => {
  //     const formattedStartDate = formatDate(curr.startDate)
  //     if (!acc[formattedStartDate]) {
  //       acc[formattedStartDate] = {
  //         dayIndex:
  //           formattedStartDates.findIndex(
  //             (date) => formattedStartDate === date
  //           ) + 1,
  //         stages: []
  //       }
  //     } else {
  //       acc[formattedStartDate].dayIndex =
  //         formattedStartDates.findIndex((date) => formattedStartDate === date) +
  //         1
  //     }

  //     acc[formattedStartDate].stages.push(curr.location.name)
  //     acc[formattedStartDate].stages = Array.from(
  //       new Set(acc[formattedStartDate].stages)
  //     ).sort((a, b) => {
  //       const aI = locations.findIndex((loc) => loc.name === a)
  //       const bI = locations.findIndex((loc) => loc.name === b)
  //       return aI - bI
  //     })
  //     return acc
  //   }, {})
  // }, [agenda, locations])
  // --------------------------------

  // When it is between first & last time, scroll to currentTime, else scroll to first tim

  // USE EFFECTS
  const timelineRef = useRef(null)
  const [scrollLeft] = useState(0)
  const [groupedAgenda, setGroupedAgenda] = useState({})
  const [currentTime, setCurrentTime] = useState({ c1: 0, c2: 0 }) // Controlled by CurrentTimeIndicator

  const filteredLocations = useMemo(() => {
    const selectedDayIndex = selectedDay.split('Day ')[1]
    const formattedSelectedDate = Object.keys(agendaItemDayMap).find(
      (formattedDate) =>
        Number(agendaItemDayMap[formattedDate].dayIndex) ===
        Number(selectedDayIndex)
    )
    return agendaItemDayMap[formattedSelectedDate].stages
  }, [agendaItemDayMap, selectedDay])

  useEffect(() => {
    setGroupedAgenda(
      agenda.reduce((acc, item) => {
        // I wanna change this key to be the startDate
        const dayName = `Day ${
          agendaItemDayMap[formatDate(item.startDate)].dayIndex
        }`
        if (!acc[dayName]) {
          acc[dayName] = { stages: {} }
        }

        if (!acc[dayName].stages[item.location.name]) {
          acc[dayName].stages[item.location.name] = {
            order: agendaItemDayMap[formatDate(item.startDate)].stages.indexOf(
              item.location.name
            ), // this order wants to be the index of an array of dayName seperated locations
            items: []
          }
        }

        const { c1, c2 } = mapToGridColumns(item)
        acc[dayName].stages[item.location.name].items.push({ ...item, c1, c2 })
        acc[dayName].stages[item.location.name].items.sort(
          (a, b) => new Date(a.startDate) - new Date(b.startDate)
        )
        return acc
      }, {})
    )
  }, [agenda, filteredLocations, agendaItemDayMap])

  // --------------------------------

  // Ai generated
  const mapToGridColumns = (item) => {
    const d1 = new Date(item.startDate)
    const d2 = new Date(item.endDate)
    const c1 = d1.getHours() * 12 + Math.floor(d1.getMinutes() / 5) + 1
    const c2 = d2.getHours() * 12 + Math.floor(d2.getMinutes() / 5) + 1 - c1
    return { c1, c2 }
  }

  // SETTINGS
  const gridSettings = {
    gridColumns: 24 * (60 / 5), // 5 minute columns
    gridRows: filteredLocations.length, // Unique stages
    numTimeLabels: (24 * (60 / 5)) / 6, // 30 minute intervals
    timelineWidth: 15000,
    timelineHeight: 150 + 100 * (filteredLocations.length - 1),
    timelinePaddingTop: 30,
    timelinePaddingBottom: 20,
    timelineLabelsHeight: 10,
    timelineLabelsWidth: 100
  }

  const getInitialScrollLeft = useCallback(
    (timelineRefWidth = 0) => {
      const sortedAgenda = event.agenda.sort((a, b) => {
        return new Date(a.startDate) - new Date(b.startDate)
      })
      const firstDate = new Date(sortedAgenda[0].startDate)
      const lastDate = new Date(sortedAgenda[sortedAgenda.length - 1].endDate)

      const totalMinutes = 24 * 60 // Total minutes in a day
      const firstMinutes = getMinutesFromMidnight(firstDate)
      const lastMinutes = getMinutesFromMidnight(lastDate)
      const nowMinutes = getMinutesFromMidnight(new Date()) // new Date() // Current time

      let date, offset
      if (nowMinutes >= firstMinutes && nowMinutes <= lastMinutes) {
        date = new Date()
        offset =
          (timelineRefWidth + 64) /* From spacing between timeline & side */ /
            2 -
          gridSettings.timelineLabelsWidth
      } else {
        date = firstDate
        offset = gridSettings.timelineLabelsWidth
      }

      const initialMinutes = getMinutesFromMidnight(date)
      const initialLeft =
        (initialMinutes / totalMinutes) * gridSettings.timelineWidth
      return initialLeft - offset
    },
    [event.agenda, gridSettings.timelineWidth, gridSettings.timelineLabelsWidth]
  )

  useEffect(() => {
    if (timelineRef?.current) {
      const scrollLeft = getInitialScrollLeft(timelineRef.current.offsetWidth)
      timelineRef.current.scrollLeft = scrollLeft
    }
  }, [timelineRef, scrollLeft, getInitialScrollLeft])

  if (Object.keys(groupedAgenda).length === 0) return null

  return (
    <div className={styles.Timeline} ref={timelineRef}>
      <ScrollArea {...gridSettings} locations={filteredLocations}>
        <CurrentTimeIndicator
          timelineWidth={gridSettings.timelineWidth}
          gridColumns={gridSettings.gridColumns}
          onUpdate={setCurrentTime}
          currentTime={currentTime}
        />
        {Object.keys(groupedAgenda[selectedDay].stages).map((location, li) => {
          const sortedAgenda = groupedAgenda[selectedDay].stages[location]
          return (
            <>
              {sortedAgenda.items.map((item, ii) => {
                const timeRange = `${item.localeSafeStartTime} - ${item.localeSafeEndTime} (${timezoneAbbreviation})`
                return (
                  <TimelineBox
                    {...item}
                    r1={sortedAgenda.order + 2}
                    r2={1}
                    key={`${li}_${ii}`}
                    currentTime={currentTime}
                    timezone={timezone}
                    registerButtonClassName={_registerButtonClassName}
                    registerAction={_registerAction}
                    timeRange={timeRange}
                  />
                )
              })}
            </>
          )
        })}
        <TimelineLabels {...gridSettings} />
      </ScrollArea>
    </div>
  )
}

Timeline.propTypes = {
  event: object,
  sortedAgenda: array,
  agendaItemDayMap: object,
  selectedDay: string
}

const StageLabels = ({
  timelineHeight,
  timelinePaddingTop,
  timelinePaddingBottom,
  timelineLabelsHeight,
  timelineLabelsWidth,
  locations
}) => {
  return (
    <div
      className={styles.StageLabels}
      style={{ width: timelineLabelsWidth + 'px' }}
    >
      <div
        className={styles.StageLabelsInner}
        style={{
          height:
            timelineHeight - timelinePaddingBottom - timelinePaddingTop + 'px',
          position: 'relative',
          top: timelineLabelsHeight + 'px'
        }}
      >
        {locations.map((location, i) => (
          <div
            key={i}
            className={styles.StageLabel}
            style={{ height: 100 / locations.length + '%' }}
          >
            <Type className={styles.StageLabelInner} size={['body3', 'body3']}>
              {location}
            </Type>
          </div>
        ))}
      </div>
    </div>
  )
}

StageLabels.propTypes = {
  ...gridSettingTypes,
  locations: array
}

const TimelineLabels = ({ gridColumns, gridRows, numTimeLabels }) => {
  const spanWidth = gridColumns / numTimeLabels

  // Ai generated
  const getTimeLabel = (index) => {
    const totalMinutes = 1440 // Total minutes in a day
    const interval = totalMinutes / numTimeLabels // Interval in minutes
    const minutes = index * interval
    const hours = Math.floor(minutes / 60)
    const mins = Math.floor(minutes % 60)
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`
  }

  const timeLabels = []
  for (var i = 0; i < numTimeLabels; i++) {
    timeLabels.push({
      index: i,
      c1: i * spanWidth + 1,
      c2: spanWidth,
      time: getTimeLabel(i)
    })
  }

  return (
    <>
      {timeLabels.map((label, i) => (
        <div
          key={i}
          className={styles.TimeLabel}
          style={{
            gridColumn: `${label.c1} / span ${label.c2} `,
            gridRow: '1 / span 1'
          }}
        >
          <span className={styles.TimeLabelValue}>
            <Type as="span" size={['body3', 'body3']}>
              {label.time}
            </Type>
          </span>
        </div>
      ))}
    </>
  )
}

TimelineLabels.propTypes = {
  ...gridSettingTypes
}

// I need to create a table. But this is going to be much more like a grid. now can i use the wonders of css grid?
// I think I may be able to, what about dynmaic grid template? Hmmm lets give that a try.
// I want to create for example n no. 1fr columns

const Box = ({ children, c1, c2 }) => {
  return (
    <div
      className={styles.Box}
      style={{ gridColumn: `${c1} / span ${c2}`, gridRow: '2 / span 1' }}
    />
  )
}

Box.propTypes = {
  children: object,
  c1: number,
  c2: number
}

const ScrollArea = (props) => {
  const {
    children,
    gridColumns,
    gridRows,
    timelineWidth,
    timelineHeight,
    timelinePaddingTop,
    timelinePaddingBottom,
    timelineLabelsHeight,
    locations
  } = props
  return (
    <div
      className={styles.ScrollArea}
      style={{
        width: timelineWidth + 'px',
        height: timelineHeight + 'px',
        paddingTop: timelinePaddingTop + 'px',
        paddingBottom: timelinePaddingBottom + 'px'
      }}
    >
      <StageLabels {...props} locations={locations} />
      <div
        className={styles.ScrollAreaInner}
        style={{
          gridTemplateColumns: `repeat(${gridColumns}, 1fr)`,
          gridTemplateRows: `${timelineLabelsHeight}px repeat(${gridRows}, 3fr)`
        }}
      >
        {children}
      </div>
    </div>
  )
}

ScrollArea.propTypes = {
  children: object,
  locations: array,
  ...gridSettingTypes
}

const TimelineRow = ({ children, columns }) => {
  return (
    <div
      className={styles.TimelineRow}
      style={{ gridTemplateColumns: columns.map((c) => c.size).join(' ') }}
    >
      {children}
    </div>
  )
}

TimelineRow.propTypes = {
  children: object,
  columns: array
}
